/**
 * Copyright © Ircam Amplify 2024-2026
 */
import { API, fetchWebhookLogs } from '@/api'
import { SortTableHeader } from '@/components/jobui/SortTableHeader'
import StatusComp from '@/components/jobui/StatusComp'
import { getModuleDisplayName } from '@/components/ModuleConfig/ModuleConfigList'
import TopBar from '@/components/Topbar'
import { getUserState, useSelector } from '@/store'
import { fetchAuthSession } from 'aws-amplify/auth'
import { useEffect, useState } from 'react'
import {
  BiHive,
  BiLoaderAlt,
  BiLoaderCircle,
  BiMessage,
  BiSolidWatch,
  BiTimer,
} from 'react-icons/bi'
import { FaCircleArrowRight } from 'react-icons/fa6'
import { useParams } from 'react-router'

const columnHeaderList = [
  { name: 'Module', icon: <BiHive /> },
  { name: 'Status', icon: <BiLoaderCircle /> },
  { name: 'Message', icon: <BiMessage /> },
  { name: 'Response duration', icon: <BiTimer /> },
]

export const WebhookLogs = () => {
  const { webhookId } = useParams()

  const { webhooks, webhookLogList, lastWebhookLogId } =
    useSelector(getUserState)
  const webhook = (webhooks ?? [])?.find((elt) => elt?.id === webhookId)

  const [loading, setLoading] = useState(true)
  const [testing, setTesting] = useState(false)
  const [testStatus, setTestStatus] = useState(undefined)
  const [moreLoading, setMoreLoading] = useState(false)

  useEffect(() => {
    const getLogs = async () => {
      if (webhookId !== undefined) {
        await fetchWebhookLogs({ webhookId, refreshing: true })
        setLoading(false)
      }
    }
    getLogs()
  }, [webhookId])

  const onTestClick = async (url: string) => {
    if (!testing) {
      setTesting(true)
      setTestStatus(undefined)
      const { data } = await API(await fetchAuthSession()).post(
        `/webhook/${webhookId}/test`,
        { url }
      )
      setTestStatus(data?.status)
      setTesting(false)
    }
  }

  const onLoadMoreClick = async () => {
    if (!moreLoading && webhookId !== undefined) {
      setMoreLoading(true)
      await fetchWebhookLogs({ webhookId, refreshing: false })
      setMoreLoading(false)
    }
  }

  return (
    <>
      <TopBar
        title={webhook?.name ?? ''}
        description={webhook?.url ?? ''}
        className="!mb-0"
      />
      <div className="flex items-center gap-4 mt-4 mb-10 w-11/12">
        <button
          type="button"
          className={`bigBtn !text-white !m-0 !text-[16px] ${testing ? 'disabled' : ''}`}
          onClick={() => onTestClick(webhook?.url ?? '')}
        >
          {testing ? (
            <BiLoaderAlt className="loadingIcon text-white" />
          ) : (
            <FaCircleArrowRight />
          )}
          Test
        </button>
        <p
          className={
            testStatus === 'SUCCESS'
              ? 'text-notification-ok-text'
              : testStatus === 'ERROR'
                ? 'text-notification-ko-text'
                : ''
          }
        >
          {testStatus ?? ''}
        </p>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="jobui-table">
          <table>
            <thead className="sticky [inset-block-start:0] bg-white">
              <tr>
                <SortTableHeader
                  name={'Date'}
                  icon={<BiSolidWatch />}
                  webhookId={webhookId}
                />
                {columnHeaderList.map(({ name, icon }) => (
                  <th key={name} className="resultCol">
                    <div className="SortTableHeader">
                      {icon}
                      {name}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(webhookLogList ?? []).map(
                ({
                  id,
                  date,
                  service_name,
                  status,
                  message,
                  duration_call,
                }) => (
                  <tr key={id}>
                    <td>{new Date(date ?? 0).toLocaleString()}</td>
                    <td>{getModuleDisplayName(service_name)}</td>
                    <td>
                      <StatusComp
                        status={(status ?? '').toLowerCase()}
                        forWebhooks={true}
                      />
                    </td>
                    <td className="max-w-[600px]">
                      <p className="text-ellipsis overflow-hidden">
                        {message ?? ''}
                      </p>
                    </td>
                    <td>{duration_call} ms</td>
                  </tr>
                )
              )}
            </tbody>
          </table>
          {(webhookLogList ?? []).length > 0 &&
            lastWebhookLogId !== undefined && (
              <button
                type="button"
                className="!mt-5 !py-2 !px-4 button-dark !rounded-full"
                disabled={moreLoading}
                onClick={onLoadMoreClick}
              >
                <div className="button-label">
                  {moreLoading ? 'Loading...' : 'Load more'}
                </div>
              </button>
            )}
        </div>
      )}
    </>
  )
}
